import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { isMSP } from "../../helpers/util_helper";
import {
  getMarginRecords,
  getMarginSummary,
  clearMarginState,
  clearMarginMessages,
} from "../../store/marginManagement/marginSlice";
import MarginRecordModal from "./components/MarginRecordModal";
import DeleteConfirmationDialog from "./components/DeleteConfirmationDialog";

const MarginDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { orgCode } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    marginRecords,
    isFetchingMarginRecords,
    fetchMarginRecordsError,
    createMarginRecordSuccess,
    createMarginRecordError,
    updateMarginRecordSuccess,
    updateMarginRecordError,
    deleteMarginRecordSuccess,
    deleteMarginRecordError,
    marginSummary,
  } = useSelector((state) => state.margin);

  // Check if user is MSP
  const isMSPUser = isMSP();

  useEffect(() => {
    // Redirect if not MSP user
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }

    // Redirect if no orgCode provided
    if (!orgCode) {
      history.push("/customer-management");
      return;
    }

    return () => {
      dispatch(clearMarginState());
    };
  }, [dispatch, isMSPUser, history, orgCode]);

  // Fetch margin summary to get customer name
  useEffect(() => {
    dispatch(getMarginSummary());
  }, [dispatch]);

  // Fetch margin records when component mounts or orgCode changes
  useEffect(() => {
    if (orgCode && orgCode.trim() !== "") {
      console.log(
        "🔄 Component: Fetching margin records for org:",
        orgCode
      );
      dispatch(getMarginRecords(orgCode));
      // Set the fetch time when making the request
      setLastFetchTime(new Date());
    } else {
      // Clear data when no organization is selected
      setLastFetchTime(null);
    }
  }, [orgCode, dispatch]);

  // Handle success/error messages
  useEffect(() => {
    if (createMarginRecordSuccess) {
      showSnackbar("Customer record created successfully!", "success");
      // Refresh the list after successful creation
      if (orgCode) {
        dispatch(getMarginRecords(orgCode));
        setLastFetchTime(new Date());
      }
      // Clear success message
      dispatch(clearMarginMessages());
    }
    if (createMarginRecordError) {
      // Error is already displayed in the modal, no need for snackbar
      // Clear error message after a delay to allow user to see it in modal
      setTimeout(() => {
        dispatch(clearMarginMessages());
      }, 5000); // 5 second delay
    }
    if (updateMarginRecordSuccess) {
      showSnackbar("Customer record updated successfully!", "success");
      // Close modal and clear editing state
      setIsModalOpen(false);
      setEditingRecord(null);
      // Refresh the list after successful update
      if (orgCode) {
        dispatch(getMarginRecords(orgCode));
        setLastFetchTime(new Date());
      }
      // Clear success message
      dispatch(clearMarginMessages());
    }
    if (updateMarginRecordError) {
      // Error is already displayed in the modal, no need for snackbar
      // Clear error message after a delay to allow user to see it in modal
      setTimeout(() => {
        dispatch(clearMarginMessages());
      }, 5000); // 5 second delay
    }
    if (deleteMarginRecordSuccess) {
      showSnackbar("Customer record deleted successfully!", "success");
      // Close delete dialog and clear delete state
      setDeleteDialogOpen(false);
      setRecordToDelete(null);
      // Refresh the list after successful deletion
      if (orgCode) {
        dispatch(getMarginRecords(orgCode));
        setLastFetchTime(new Date());
      }
      // Clear success message
      dispatch(clearMarginMessages());
    }
    if (deleteMarginRecordError) {
      showSnackbar(deleteMarginRecordError, "error");
      // Clear error message after a delay to allow user to see it
      setTimeout(() => {
        dispatch(clearMarginMessages());
      }, 5000); // 5 second delay
    }
  }, [
    createMarginRecordSuccess,
    createMarginRecordError,
    updateMarginRecordSuccess,
    updateMarginRecordError,
    deleteMarginRecordSuccess,
    deleteMarginRecordError,
    dispatch,
    orgCode,
  ]);

  const handleAddRecord = () => {
    setEditingRecord(null);
    setIsModalOpen(true);
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setIsModalOpen(true);
  };

  const handleDeleteRecord = (record) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  const handleBackToDashboard = () => {
    history.push("/customer-management");
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Get customer name from margin summary data
  const getCustomerName = () => {
    if (!marginSummary?.data || !orgCode) return orgCode;
    
    const customer = marginSummary.data.find(c => c.orgcode === orgCode);
    return customer ? customer.customer_name : orgCode;
  };

  // Format the last fetch time for display
  const formatLastFetchTime = () => {
    if (!lastFetchTime) return null;
    
    return lastFetchTime.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Helper function to parse date strings consistently
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    
    // If it's already a Date object, return it
    if (dateString instanceof Date) return dateString;
    
    // If it's an ISO string with time, extract just the date part
    if (typeof dateString === 'string' && dateString.includes('T')) {
      const datePart = dateString.split('T')[0];
      return new Date(datePart + 'T00:00:00');
    }
    
    // Otherwise, create a new Date object
    return new Date(dateString);
  };

  // Helper function to get date in YYYY-MM-DD format for comparison
  const getDateString = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Define columns for the DataGrid
  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 280,
      renderCell: (params) => {
        const today = new Date();
        const endDate = parseDateString(params.row.effectiveEndDate);
        const startDate = parseDateString(params.row.effectiveStartDate);
        
        // Debug logging
        console.log("🔍 Date Debug for:", params.row.name, {
          today: today.toISOString(),
          todayLocal: today.toLocaleDateString(),
          effectiveEndDate: params.row.effectiveEndDate,
          endDate: endDate?.toISOString(),
          endDateLocal: endDate?.toLocaleDateString(),
          effectiveStartDate: params.row.effectiveStartDate,
          startDate: startDate?.toISOString(),
          startDateLocal: startDate?.toLocaleDateString(),
        });
        
        let status = "Unknown";
        let statusColor = "#6b7280";
        let backgroundColor = "#f3f4f6";
        
        if (endDate && startDate) {
          // Get dates in YYYY-MM-DD format for string comparison
          const todayStr = getDateString(today);
          const startDateStr = getDateString(startDate);
          const endDateStr = getDateString(endDate);
          
          console.log("🔍 String Comparison Debug:", {
            todayStr,
            startDateStr,
            endDateStr,
            isActive: todayStr >= startDateStr && todayStr <= endDateStr,
            isExpired: todayStr > endDateStr,
            isUpcoming: todayStr < startDateStr,
          });
          
          if (todayStr >= startDateStr && todayStr <= endDateStr) {
            status = "Active";
            statusColor = "#059669";
            backgroundColor = "#d1fae5";
          } else if (todayStr > endDateStr) {
            status = "Expired";
            statusColor = "#dc2626";
            backgroundColor = "#fee2e2";
          } else if (todayStr < startDateStr) {
            status = "Upcoming";
            statusColor = "#d97706";
            backgroundColor = "#fed7aa";
          }
        }
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: "#1e293b",
                fontSize: "0.875rem",
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              {params.value}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.25,
                borderRadius: "8px",
                backgroundColor: backgroundColor,
                border: `1px solid ${statusColor}20`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: statusColor,
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  textTransform: "uppercase",
                  letterSpacing: "0.025em",
                }}
              >
                {status}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "margin",
      headerName: "Discount/Markup (%)",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const marginValue = parseFloat(params.value) || 0;
        const isMarkup = marginValue < 0;
        const absoluteValue = Math.abs(marginValue);
        const badgeText = isMarkup ? "Markup" : "Discount";
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#374151",
                fontSize: "0.875rem",
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              {absoluteValue}%
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.5,
                borderRadius: "12px",
                backgroundColor: "#f3f4f6",
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#6b7280",
                  fontSize: "0.625rem",
                  fontWeight: 500,
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                {badgeText}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "dollarMargin",
      headerName: "MSP Margin ($)",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#059669",
            fontSize: "0.875rem",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          ${params.value || "0.00"}
        </Typography>
      ),
    },
    {
      field: "effectiveStartDate",
      headerName: "Start Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <Typography
            variant="body2"
            sx={{
              color: "#374151",
              fontSize: "0.875rem",
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "effectiveEndDate",
      headerName: "End Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <Typography
            variant="body2"
            sx={{
              color: "#374151",
              fontSize: "0.875rem",
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            fontSize: "0.875rem",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontStyle: "italic",
          }}
        >
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            fontSize: "0.875rem",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontStyle: "italic",
          }}
        >
          {params.value || "N/A"}
        </Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated On",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            fontSize: "0.875rem",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {params.value || "N/A"}
        </Typography>
      ),
    },

    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 120,
      sortable: false,
             renderCell: (params) => {
         // Check if record is expired, active, or upcoming
         const today = new Date();
         const endDate = parseDateString(params.row.effectiveEndDate);
         const startDate = parseDateString(params.row.effectiveStartDate);
         
         let isExpired = false;
         let isActive = false;
         let isUpcoming = false;
         if (endDate && startDate) {
           const todayStr = getDateString(today);
           const startDateStr = getDateString(startDate);
           const endDateStr = getDateString(endDate);
           isExpired = todayStr > endDateStr;
           isActive = todayStr >= startDateStr && todayStr <= endDateStr;
           isUpcoming = todayStr < startDateStr;
         }
         
         // Only allow editing for upcoming records
         const canEdit = isUpcoming;
        
        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Tooltip
              title={!canEdit ? "Please Contact support : support.cloudcatcher@cloudnovatech.com" : "Edit record"}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    if (!canEdit) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    handleEditRecord(params.row);
                  }}
                  disabled={!canEdit}
                  sx={{
                    color: canEdit ? "#3b82f6" : "#9ca3af",
                    backgroundColor: canEdit ? "#eff6ff" : "#f3f4f6",
                    "&:hover": {
                      backgroundColor: canEdit ? "#dbeafe" : "#f3f4f6",
                      color: canEdit ? "#2563eb" : "#9ca3af",
                    },
                    "&:disabled": {
                      color: "#9ca3af",
                      backgroundColor: "#f3f4f6",
                      cursor: "not-allowed",
                    },
                    width: 32,
                    height: 32,
                    opacity: canEdit ? 1 : 0.6,
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            
            <Tooltip
              title={!isUpcoming ? "Please Contact support : support.cloudcatcher@cloudnovatech.com" : "Delete record"}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    if (!isUpcoming) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    handleDeleteRecord(params.row);
                  }}
                  disabled={!isUpcoming}
                  sx={{
                    color: isUpcoming ? "#ef4444" : "#9ca3af",
                    backgroundColor: isUpcoming ? "#fef2f2" : "#f3f4f6",
                    "&:hover": {
                      backgroundColor: isUpcoming ? "#fee2e2" : "#f3f4f6",
                      color: isUpcoming ? "#dc2626" : "#9ca3af",
                    },
                    "&:disabled": {
                      color: "#9ca3af",
                      backgroundColor: "#f3f4f6",
                      cursor: "not-allowed",
                    },
                    width: 32,
                    height: 32,
                    opacity: isUpcoming ? 1 : 0.6,
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
           
          </Box>
        );
      },
    },
  ];

  // Transform margin records for DataGrid and sort by status priority, then by start date
  const rows = marginRecords
    .map((record, index) => {
      const today = new Date();
      const endDate = parseDateString(record.effectiveEndDate);
      const startDate = parseDateString(record.effectiveStartDate);
      
      let statusPriority = 3; // Unknown/Default
      if (endDate && startDate) {
        // Get dates in YYYY-MM-DD format for string comparison
        const todayStr = getDateString(today);
        const startDateStr = getDateString(startDate);
        const endDateStr = getDateString(endDate);
        
        // Debug logging for sorting
        console.log("🔍 Sorting String Debug for:", record.name, {
          todayStr,
          startDateStr,
          endDateStr,
          isActive: todayStr >= startDateStr && todayStr <= endDateStr,
          isExpired: todayStr > endDateStr,
          isUpcoming: todayStr < startDateStr,
        });
        
        if (todayStr >= startDateStr && todayStr <= endDateStr) {
          statusPriority = 1; // Active - highest priority
        } else if (todayStr > endDateStr) {
          statusPriority = 3; // Expired - lowest priority
        } else if (todayStr < startDateStr) {
          statusPriority = 2; // Upcoming - medium priority
        }
      }
      
      return {
        id: record.id || index,
        ...record,
        statusPriority,
      };
    })
    .sort((a, b) => {
      // First sort by status priority (Active -> Upcoming -> Expired)
      if (a.statusPriority !== b.statusPriority) {
        return a.statusPriority - b.statusPriority;
      }
      
      // Within same status, sort by start date
      const dateA = a.effectiveStartDate ? new Date(a.effectiveStartDate) : new Date(0);
      const dateB = b.effectiveStartDate ? new Date(b.effectiveStartDate) : new Date(0);
      

      
      // For Active records: newest start date first (most recently started)
      // For Upcoming records: earliest start date first (next upcoming)
      // For Expired records: newest start date first (most recently expired)
      if (a.statusPriority === 1) {
        // Active records - newest start date first
        return dateB - dateA;
      } else if (a.statusPriority === 2) {
        // Upcoming records - earliest start date first (next upcoming)
        return dateA - dateB;
      } else {
        // Expired records - newest start date first (most recently expired)
        return dateB - dateA;
      }
    });

  if (!isMSPUser) {
    return null; // Will redirect to dashboard
  }

  return (
    <Box
      sx={{
        padding: "1.5rem 2rem",
        mb: "3rem",
        backgroundColor: "#F9F9F9",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Subtle Header with Back Button on Right */}
      <Box sx={{ 
        mb: 3, 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        borderBottom: "1px solid #e2e8f0",
        pb: 2
      }}>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              color: "#1e293b",
              fontSize: "1.25rem",
              letterSpacing: "-0.025em",
              mb: 0.5,
            }}
          >
            Records
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 400,
            }}
          >
            {getCustomerName()}
          </Typography>
        </Box>
        <Button
          onClick={handleBackToDashboard}
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          sx={{
            color: "#64748b",
            backgroundColor: "transparent",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "#f8fafc",
              color: "#374151",
              borderColor: "#d1d5db",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          Back
        </Button>
      </Box>

      {/* Margin Records Section */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            p: 2,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "0.875rem",
                mb: 0.5,
              }}
            >
              Records
            </Typography>
            {formatLastFetchTime() && (
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  fontStyle: "italic",
                }}
              >
                Data fetched on {formatLastFetchTime()}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRecord}
            sx={{
              backgroundColor: "#3b82f6",
              color: "white",
              fontWeight: 600,
              fontSize: "0.875rem",
              padding: "10px 20px",
              borderRadius: "8px",
              textTransform: "none",
              boxShadow:
                "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
              "&:hover": {
                backgroundColor: "#2563eb",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              },
              "&:active": {
                backgroundColor: "#1d4ed8",
              },
            }}
          >
            Add Record
          </Button>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {fetchMarginRecordsError && (
            <Box
              sx={{
                mb: 3,
                p: 3,
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #f87171",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#991b1b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {fetchMarginRecordsError}
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {!isFetchingMarginRecords && marginRecords.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                px: 4,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  backgroundColor: "#f1f5f9",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <AddIcon sx={{ color: "#64748b", fontSize: 32 }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#1e293b",
                  fontWeight: 600,
                  mb: 1,
                  fontSize: "1.125rem",
                }}
              >
                No records found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  mb: 3,
                  fontSize: "0.875rem",
                }}
              >
                Get started by creating your first record
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddRecord}
                sx={{
                  backgroundColor: "#3b82f6",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#2563eb",
                  },
                }}
              >
                Create First Record
              </Button>
            </Box>
          )}

          {/* Loading Spinner */}
          {(isFetchingMarginRecords || (orgCode && !lastFetchTime)) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
              <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  color: "#64748b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {isFetchingMarginRecords ? "Loading records..." : "Refreshing data..."}
              </Typography>
            </Box>
          )}

          {/* Data Grid */}
          {!isFetchingMarginRecords && !fetchMarginRecordsError && marginRecords.length > 0 && (
            <Box
              sx={{
                height: 500,
                "& .MuiDataGrid-root": {
                  border: "none",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#F5F5F5",
                  borderBottom: "1px solid #e2e8f0",
                  "& .MuiDataGrid-columnHeader": {
                    borderRight: "1px solid #e2e8f0",
                    "&:last-child": {
                      borderRight: "none",
                    },
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 700,
                    color: "#333",
                    fontSize: "15px",
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f3f4f6",
                  fontSize: "0.875rem",
                  color: "#374151",
                  "&:focus": {
                    outline: "none",
                  },
                  padding: "8px 16px",
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                  },
                  "&:nth-of-type(even)": {
                    backgroundColor: "#fafafa",
                  },
                  cursor: "pointer",
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: "white",
                },
                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: "#f8fafc",
                  borderTop: "1px solid #e2e8f0",
                },
              }}
            >
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={isFetchingMarginRecords}
                disableSelectionOnClick
                disableColumnMenu
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 700,
                  },
                  "& .MuiDataGrid-cell": {
                    py: 1,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#F5F5F5",
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Add/Edit Modal */}
      <MarginRecordModal
        open={isModalOpen}
        onClose={handleCloseModal}
        record={editingRecord}
        requestingOrgCode={orgCode}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        record={recordToDelete}
      />

      {/* MUI Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MarginDetails;
